import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

const UserSelect = ({ value, onChange, placeholder: placeholderProp, className = '' }) => {
  const { t } = useTranslation();
  const placeholder = placeholderProp || t('common.search_employee_placeholder');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Load all employees on mount (limit 200 for dropdown)
  useEffect(() => {
    setLoading(true);
    fetch(`/api/employees?limit=200&lang=zh`)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setUsers(data.data || []);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Debounced search
  const handleSearch = useCallback((term) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams({ limit: '200', lang: 'zh' });
      if (term.trim()) params.set('search', term.trim());
      fetch(`/api/employees?${params.toString()}`)
        .then(res => res.json())
        .then(data => {
          if (data.status === 'success') {
            setUsers(data.data || []);
          }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }, 300);
  }, []);

  // Set selected user when value prop changes
  useEffect(() => {
    if (!value && value !== 0) {
      setSelectedUser(null);
      return;
    }
    const found = users.find(u => String(u.entity_id) === String(value));
    if (found) setSelectedUser(found);
  }, [value, users]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (user) => {
    setSelectedUser(user);
    setIsOpen(false);
    setSearch('');
    onChange(String(user.entity_id));
  };

  const handleClear = () => {
    setSelectedUser(null);
    setSearch('');
    onChange('');
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  };

  const filteredUsers = search.trim()
    ? users.filter(u => {
        const attrs = u.attributes || {};
        const fullName = `${attrs.first_name || ''} ${attrs.last_name || ''}`.toLowerCase();
        const email = (u.email || '').toLowerCase();
        const term = search.toLowerCase();
        return fullName.includes(term) || email.includes(term);
      })
    : users;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Trigger button */}
      <div
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm cursor-pointer flex items-center justify-between hover:border-slate-400 dark:hover:border-slate-500 transition-colors bg-white dark:bg-slate-800"
        onClick={toggleOpen}
      >
        {selectedUser ? (
          <span className="text-slate-800 dark:text-white truncate">
            {selectedUser.entity_id} - {(selectedUser.attributes?.first_name || '')}{(selectedUser.attributes?.last_name || '')}
          </span>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
        <div className="flex items-center gap-1">
          {selectedUser && (
            <X
              size={14}
              className="text-slate-400 hover:text-red-500 cursor-pointer"
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
            />
          )}
          <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-[9999] max-h-[300px] flex flex-col">
          {/* Search box */}
          <div className="p-2 border-b border-slate-100 dark:border-slate-700">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); handleSearch(e.target.value); }}
                placeholder={t("common.search_employee_placeholder")}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-md outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
              />
            </div>
          </div>

          {/* Options list */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-sm text-slate-400">{t("common.loading")}</div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-400">{t("common.no_matching_employees")}</div>
            ) : (
              filteredUsers.map(user => {
                const isSelected = selectedUser && String(user.entity_id) === String(selectedUser.entity_id);
                return (
                  <div
                    key={user.entity_id}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors ${
                      isSelected ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300'
                    }`}
                    onClick={() => handleSelect(user)}
                  >
                    <span className="font-medium">{user.entity_id} - {(user.attributes?.first_name || '')}{(user.attributes?.last_name || '')}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSelect;
